import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const StorySection = () => {
    const sectionRef = useRef(null);
    const stepsRef = useRef([]);

    const storySteps = [
        {
            icon: 'fa-lightbulb',
            title: 'Dream of Studying Abroad',
            description: 'Every great journey begins with a dream. Imagine yourself walking through world-class universities, experiencing new cultures, and building a global career.',
            color: 'from-yellow-400 to-orange-500',
        },
        {
            icon: 'fa-globe',
            title: 'Choose Your Destination',
            description: 'With expert guidance, find the perfect country that matches your academic goals, budget, and career aspirations. From Australia to Canada, we help you decide.',
            color: 'from-blue-400 to-cyan-500',
        },
        {
            icon: 'fa-file-alt',
            title: 'Application Process',
            description: 'Our team guides you through every step - from university selection to SOP writing, document preparation, and application submission.',
            color: 'from-green-400 to-emerald-500',
        },
        {
            icon: 'fa-passport',
            title: 'Visa Success',
            description: 'With a 95% success rate, we ensure your visa application is flawless. Our experts handle all documentation and interview preparation.',
            color: 'from-primary-red to-primary-light',
        },
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            stepsRef.current.forEach((step, index) => {
                gsap.fromTo(
                    step,
                    {
                        opacity: 0,
                        y: 80,
                        scale: 0.9,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 1,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: step,
                            start: 'top 85%',
                            end: 'top 50%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="story-section py-20 bg-gradient-to-b from-white to-lightGray"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-route text-primary-red text-2xl"></i>
                        <h2 className="section-title">Your Journey From Dream to Destination</h2>
                    </div>
                    <p className="section-subtitle">
                        Follow the path that thousands of successful students have taken
                    </p>
                </div>

                {/* Story Steps */}
                <div className="relative">
                    {/* Connecting Line */}
                    <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-red/20 via-primary-red to-primary-red/20 transform -translate-x-1/2"></div>

                    <div className="space-y-16 lg:space-y-24">
                        {storySteps.map((step, index) => (
                            <div
                                key={index}
                                ref={(el) => (stepsRef.current[index] = el)}
                                className={`story-step flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                                    }`}
                            >
                                {/* Content */}
                                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                                    <div className="inline-flex items-center gap-2 bg-primary-red/10 text-primary-red px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                        <span>Step {index + 1}</span>
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-black mb-4">
                                        {step.title}
                                    </h3>
                                    <p className="text-gray text-lg leading-relaxed">
                                        {step.description}
                                    </p>
                                </div>

                                {/* Icon */}
                                <div className="relative">
                                    <div className={`w-24 h-24 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center shadow-xl`}>
                                        <i className={`fas ${step.icon} text-white text-3xl`}></i>
                                    </div>
                                    {/* Pulse effect */}
                                    <div className={`absolute inset-0 w-24 h-24 bg-gradient-to-br ${step.color} rounded-full animate-ping opacity-20`}></div>
                                </div>

                                {/* Spacer for alternating layout */}
                                <div className="flex-1 hidden lg:block"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="text-center mt-16">
                    <p className="text-gray text-lg mb-6">
                        Ready to start your journey?
                    </p>
                    <a
                        href="https://wa.me/919000329999?text=Hi%2C%20I%20would%20like%20to%20start%20my%20study%20abroad%20journey."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        <i className="fas fa-arrow-right"></i>
                        Begin Your Story
                    </a>
                </div>
            </div>
        </section>
    );
};

export default StorySection;
